import React, { useCallback } from 'react';
import SwivuModal from "./editors/SwivuModal.jsx";
import {WIDGET_KEYS} from "../../other/constants/WidgetKeys.jsx";
import EditorBase from "../../bryntum-lib/editors/base/EditorBase.jsx";
import {PERSISTENCE_STATE_KEYS} from "../../other/constants/PersistenceStateKeys.jsx";
import {
  defaultCellRenderer, showShareUrlOptions,
  showConfirmationDialog,
} from "../../other/utils/utils.jsx";
import {SwivuRichTextEditorManager} from "../../bryntum-lib/widget/SwivuRichTextEditorManager.jsx";
import {DEEP_LINK_URL_KEYS, SWIVU_RICH_TEXT_EDITOR_CLS} from "../../other/constants/OtherKeys.jsx";
import { DateHelper } from "@bryntum/schedulerpro";
import { getEntityShareLink } from '../../other/utils/otherUtils.jsx';

const headerCls = "swivu-rich-text-editor-header";

class RichTextEditorPopup extends EditorBase {
  static get $name() {
    return "RichTextEditorPopup";
  }

  static get type() {
    return WIDGET_KEYS.swivuRichTextPopup;
  }

  static get configurable() {
    return {
      title: "",
      autoClose: false,
      draggable: {
        // this needs to be given other dragging a selected text in ck editor also drags the editor
        handleSelector: `.${headerCls}`
      },
      modal: false,
      closable: true,
      minWidth: "35em",
      width: "60em",
      minHeight: "35em",
      height: "35em",
      contentElementCls: "!p-0",
      header: {
        cls: headerCls,
      },
      closeAction: "destroy",
      cls: `${SWIVU_RICH_TEXT_EDITOR_CLS} resize`,
      monitorResize: true,
      stateId: PERSISTENCE_STATE_KEYS.richTextEditorPopup,
      listeners: {
        resize: "onResizeEditorHandler",
        beforeClose: "onCloseEditorHandler",
      },
      items: {
        richTextEditorManager: {
          type: SwivuRichTextEditorManager.type,
        }
      },
      bbar: {
        contentElementCls: "!pb-0",
        items: {
          leftContainer: {
            type: "container",
            flex: 1,
            cls: "pl-[1em] text-[#6c6b6b] !gap-[0.2em] text-[0.9em]",
            items: {

              entityRecordName: {
                cls: "truncate block",
                html: ""
              },
              entityMetaInfo: {
                cls: "truncate block",
                html: ""
              },
              entityCount: {
                cls: "block",
                html: ""
              }
            }
          },
          rightContainer: {
            type: "container",
            items: {
              noteSaveButton: {
                type: "button",
                cls: "bg-[transparent] text-[#61905761] [&:not(.b-disabled)]:!text-[#228a0c] border-[1.5px] !rounded-md dark:border-[#373638]",
                text: "Save",
                icon: "fa-solid fa-floppy-disk",
                onClick: "up.onSaveChangesButton",
                disabled: true,
              },
              noteSavingButton: {
                type: "button",
                text: "Saving...",
                cls: "bg-[transparent] text-[#228a0c] border-[1.5px] !rounded-md dark:border-[#373638]",
                disabled: true,
                icon: "fa-solid fa-sync fa-spin",
                hidden: true,
              },
              noteLinkButton: {
                type: "button",
                text: "Link",
                hidden: true,
                icon: "fa-solid fa-link",
                cls: "bg-[transparent] text-[#ba810f] [&:not(.b-disabled)]:!text-[#ba810f] border-[1.5px] !rounded-md dark:border-[#373638]",
                onClick: 'up.onShareNoteButton'
              },
              noteCloseButton: {
                type: "button",
                icon: "fa-light fa-close",
                text: "Close",
                cls: 'bg-[transparent] text-[#3884e1] border-[1.5px] !rounded-md dark:border-[#373638]',
                onClick: "up.close"
              },
            }
          }
        }
      }
    }
  }

  swivuRenderTitle(note) {
    const { entityRecord, noteRecord } = note;
    this.title = `<div class="flex justify-center items-center whitespace-nowrap gap-2 h-full !overflow-visible">
      <span class="overflow-hidden text-ellipsis ml-[5.3em] text-[#7a7a7a] ${noteRecord.archived ? 'line-through': ''}">${defaultCellRenderer({value: entityRecord.name})} | ${defaultCellRenderer({value: noteRecord.name})}</span>
    </div>`
  }

  onShareNoteButton() {
    const noteShareLink = this.getNoteShareLink();
    showShareUrlOptions(noteShareLink);
  }

  switchButtonsState(isSwitch) {
    const { noteSaveButton, noteSavingButton, noteCloseButton, richTextEditorManager } = this.widgetMap;
    noteSaveButton.hidden = isSwitch;
    noteSavingButton.hidden = !isSwitch;
    noteCloseButton.hidden = isSwitch;
    richTextEditorManager.swivuSetNoteReadOnly(isSwitch);
  }

  onSaveChangesButton({isSaveAndClose = false}) {
    this.switchButtonsState(true);
    this.widgetMap.richTextEditorManager.saveNote()
      .then(() => {
        if (!this.isDestroyed) {
          const { noteSaveButton} = this.widgetMap;
          this.switchButtonsState(false);
          this.isPendingSaveNotesChanges = false;
          this.widgetMap.noteLinkButton.hidden = false;
          noteSaveButton.disabled = true;
          if (isSaveAndClose) {
            this.close();
          }
        }
      })
      .catch(() => {
        this.switchButtonsState(false);
        this.isPendingSaveNotesChanges = true;
      });

  }

  onCloseEditorHandler() {
    if (this.widgetMap.richTextEditorManager.swivuLoadNoteController) this.widgetMap.richTextEditorManager.swivuLoadNoteController.abort();

    if (this.isPendingSaveNotesChanges) {
      showConfirmationDialog({
        title: "Unsaved Changes",
        message: "You have unsaved changes. Are you sure to close the editor?",
        confirmBtnText: "Save Changes",
        cancelBtnText: "Discard Changes",
        isCloseByHeader: true,
      }).then((isCloseWithSave) => {
        if (isCloseWithSave) {
          this.onSaveChangesButton({isSaveAndClose: true});
        } else {
          this.isPendingSaveNotesChanges = false;
          this.close();
        }
      });
    } else {
      window.removeEventListener( 'beforeunload', this.preventWindowReloadListener);
    }

    if (!this.isPendingSaveNotesChanges && this.destroyBoardNoteUpdateListener) {
      this.destroyBoardNoteUpdateListener();
      this.destroyBoardNoteUpdateListener = null;
    }

    return !this.isPendingSaveNotesChanges;
  }

  addPreventWindowReloadListener() {
    // prevent window reload if there are any pending changes
    this.preventWindowReloadListener = (evt) => {
      if (this.isPendingSaveNotesChanges) {
        evt.preventDefault();
      }
    };
    window.addEventListener( 'beforeunload', this.preventWindowReloadListener);
  }

  updateRichTextEditorWordCount() {
    const { richTextEditorManager} = this.widgetMap;
    const { leftContainer } = this.bbar.widgetMap;
    const wordCount = richTextEditorManager.swivuGetWordCount();
    const wordCountHtml = `
      <div class="truncate">
        <span>Words: ${wordCount.words}</span>,
        <span>Characters: ${wordCount.characters}</span>
      </div>
    `;
    leftContainer.widgetMap.entityCount.html = wordCountHtml;
  }

  setupNoteEditorListeners(entityRecord) {
    const { noteSaveButton, richTextEditorManager} = this.widgetMap;
    // CKEditor's initialization takes time to correctly init
    richTextEditorManager.swivuCKEditorInitPromise.then(() => {
      this.updateRichTextEditorWordCount();
      richTextEditorManager.swivuAddWordCountChangeListener(() => {
        this.updateRichTextEditorWordCount();
      });
      richTextEditorManager.swivuAddNotChangeListener(() => {
        if (entityRecord.isPhantom) {
          // do not save empty record
          const isHasNoteData = richTextEditorManager.swivuGetNoteData()?.length;
          noteSaveButton.disabled = !isHasNoteData;
          this.isPendingSaveNotesChanges = isHasNoteData;
        } else {
          noteSaveButton.disabled = false;
          this.isPendingSaveNotesChanges = true;
        }
      });

      this.addPreventWindowReloadListener();
    });
  }

  async loadRecord(record, context, otherParams) {
    const { noteRecord, entityRecord } = record;

    super.editRecord(noteRecord, context, otherParams);

    this.record = noteRecord;
    this.context = context;
    this.entityRecord = entityRecord;

    this.isPendingSaveNotesChanges = false;

    const { noteSaveButton, noteLinkButton, richTextEditorManager} = this.widgetMap;
    const { leftContainer } = this.bbar.widgetMap;
    leftContainer.widgetMap.entityRecordName.html = `${entityRecord.entityVisibleName}: ${defaultCellRenderer({ value: entityRecord.name })}`;
    leftContainer.widgetMap.entityMetaInfo.html = `
      Created By: ${defaultCellRenderer({ value: noteRecord.creatorName })}&nbsp;
      Last Edit: ${defaultCellRenderer({ value: DateHelper.format(new Date(noteRecord.updatedAt), 'MM/DD/YYYY') })}&nbsp;
      Last Edit By: ${defaultCellRenderer({ value: noteRecord.updaterName })}
    `.trim();
    this.mask("Loading...");

    await richTextEditorManager.loadRecord(noteRecord, context);

    // We are using this.isDestroyed to check if our editor has already not been closed.
    // If the editor is closed before the note is loaded which is being loaded in the above
    // load record then after the resolution of the api call this function starts running again
    // since our this reference is destroyed therefore we encounter errors.
    if (!this.isDestroyed) {
      noteSaveButton.disabled = true;

      if (noteRecord.isPhantom || context.user.can(noteRecord.canEdit)) {
        this.setupNoteEditorListeners(entityRecord);
      }

      if (!noteRecord.isPhantom) {
        noteLinkButton.hidden = false;
      }

      this.swivuRenderTitle({ noteRecord, entityRecord });

      this.destroyBoardNoteUpdateListener = context.project.boardNotesStore.on({
        update() {
          this.swivuRenderTitle({ noteRecord, entityRecord });
        },
        thisObj: this,
      });

      // This needs to called right after editor opened with slight delay, otherwise the content from bottom will hide behind footer until editor is resized
      setTimeout(() => {
        this.onResizeEditorHandler({ width: this.width, height: this.height });
      }, 0)

      this.unmask();
    }
  }

  getNoteShareLink() {
    let deepLinkUrlKey, deepLinkSlug = "note";
    const { entityRecord } = this;

    if (entityRecord.isTask || entityRecord.isPlanTaskModel) {
      deepLinkSlug = "document";
      deepLinkUrlKey = DEEP_LINK_URL_KEYS.task;
    } else if (entityRecord.isResource) {
      deepLinkUrlKey = DEEP_LINK_URL_KEYS.resource;
    } else if (entityRecord.isClient) {
      deepLinkUrlKey = DEEP_LINK_URL_KEYS.client;
    }

    return getEntityShareLink({
      record: entityRecord,
      context: this.context,
      editorTabs: deepLinkSlug,
      entityType: deepLinkUrlKey,
      otherKeys: { [DEEP_LINK_URL_KEYS.note]: this.record.id },
      entityDetail: { name: this.record.name }
    })
  }

  onResizeEditorHandler(e) {
    const { width, height } = e;
    const { richTextEditorManager } = this.widgetMap;
    const editorBbarHeight = this.bbar.height;
    const editorTbarHeight = this.headerElement.clientHeight;
    // Our content body has a margin bottom applied of 1em
    // Therefore subtracting that margin from height,
    // 1em equals 15px w.r.t fontSize in body tag
    richTextEditorManager.swivuResizeEditor(width, height - 20 - editorBbarHeight - editorTbarHeight);
  }

  onWidgetDragEnd() {
    /* Hypothesis/Bug: When we are dragging text in ckEditor and dropping it outside our bryntum's editor our widgetDragEnd function is being invoked. Which triggers this `dragOverEventDetacher` function but when the drag is initiated by ckEditor this dragOverEventDetacher is undefined therefore, our editor crashes. So we are preventing event from being called if it is falsy */
    if (typeof this.dragOverEventDetacher === "function") {
      super.onWidgetDragEnd();
    }
  }
}


RichTextEditorPopup.initClass();

const SwivuRichTextEditorPopup = ({ context, record, onClose, isShow, editorInstance }) => {
  const onShowHandler = useCallback((editor, otherParams = {}) => {
  // Bug: Editor is re-rendered randomly during typing and also when socket is reconnected causing to overwrite unsaved changes in the editor
    if (otherParams.isFirstShow) {
      editor.project = context.project;
      editor.loadRecord(record, context, otherParams);
      editorInstance.editorRef = editor;
    }
  }, [context, editorInstance, record]);

  return (
    <SwivuModal
      widgetType={WIDGET_KEYS.swivuRichTextPopup}
      isShow={isShow}
      onHide={onClose}
      onShow={onShowHandler}
    />
  )
}

export default SwivuRichTextEditorPopup;
